import React, { useEffect } from 'react';
// Served from public/, the same file index.html points its static meta tags at
const defaultSeoImage = '/quilt-seo-image.png';

export interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  path?: string;
  /** Ask search engines not to index this page (unlisted pages). */
  noindex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title = "Quilt | Generative Content for Health & Wellness",
  description = "Quilt turns clinical insights into dynamic, personalized video streams to boost patient engagement, retention, and outcomes.",
  image = defaultSeoImage,
  imageAlt = "Quilt - Generative Content for Health & Wellness",
  path = "",
  noindex = false,
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Get absolute URL helper for image and path
    const getAbsoluteUrl = (urlStr: string) => {
      if (!urlStr) return "";
      if (urlStr.startsWith("http://") || urlStr.startsWith("https://")) {
        return urlStr;
      }
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const cleanPath = urlStr.startsWith("/") ? urlStr : `/${urlStr}`;
      return `${origin}${cleanPath}`;
    };

    const fullImageUrl = getAbsoluteUrl(image);
    const fullPageUrl = getAbsoluteUrl(path || (typeof window !== "undefined" ? window.location.pathname : ""));

    // Set or create meta tags in head
    const setMetaTag = (attrName: "name" | "property", attrValue: string, contentValue: string) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", contentValue);
    };

    // Standard Meta Tags
    setMetaTag("name", "description", description);
    setMetaTag("name", "image", fullImageUrl);

    // Open Graph Meta Tags (Facebook, LinkedIn, iMessage, WhatsApp, Slack, Discord, Pinterest)
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:image", fullImageUrl);
    setMetaTag("property", "og:image:secure_url", fullImageUrl);
    setMetaTag("property", "og:image:type", "image/png");
    setMetaTag("property", "og:image:width", "1200");
    setMetaTag("property", "og:image:height", "630");
    setMetaTag("property", "og:image:alt", imageAlt);
    setMetaTag("property", "og:url", fullPageUrl);
    setMetaTag("property", "og:site_name", "Quilt");

    // Twitter Card Meta Tags (Twitter / X)
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", fullImageUrl);
    setMetaTag("name", "twitter:image:src", fullImageUrl);
    setMetaTag("name", "twitter:image:alt", imageAlt);

  }, [title, description, image, imageAlt, path]);

  useEffect(() => {
    if (!noindex) return;
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(robots);
    return () => {
      robots.remove();
    };
  }, [noindex]);

  return null;
};

export default SEO;
